import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { requestDeferredApiRenderFlush } from './api-render-flush.service';

/**
 * Ensures HTTP callback delivery re-enters NgZone.
 * Deferred render flush runs after NSwag blob→JSON parse (FileReader is outside NgZone in zone.js 0.15+).
 */
@Injectable()
export class AngularZoneHttpInterceptor implements HttpInterceptor {
    constructor(private readonly ngZone: NgZone) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const request = this.withNoCacheHeadersForApiGets(req);

        return new Observable<HttpEvent<unknown>>((observer) => {
            const subscription = next.handle(request).subscribe({
                next: (event) =>
                    this.runInAngularZone(() => {
                        if (
                            event instanceof HttpResponse &&
                            this.isApiRequest(request.url) &&
                            event.body instanceof Blob
                        ) {
                            requestDeferredApiRenderFlush();
                        }
                        observer.next(event);
                    }),
                error: (error) => this.runInAngularZone(() => observer.error(error)),
                complete: () => this.runInAngularZone(() => observer.complete()),
            });

            return () => subscription.unsubscribe();
        });
    }

    private withNoCacheHeadersForApiGets(req: HttpRequest<unknown>): HttpRequest<unknown> {
        if (req.method !== 'GET' || !this.isApiRequest(req.url)) {
            return req;
        }

        return req.clone({
            setHeaders: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0',
            },
        });
    }

    private isApiRequest(url: string): boolean {
        return /\/api\//i.test(url) || /\/Abp/i.test(url);
    }

    private runInAngularZone(callback: () => void): void {
        if (NgZone.isInAngularZone()) {
            callback();
            return;
        }

        this.ngZone.run(callback);
    }
}
